import type { RouterPhaseId, UseCaseFlowDef, UseCaseFlowId } from "./types";

/** ChatResourceRouter + ChatTurnPlanner — product-accurate phases. */
export const ROUTER_PHASES: {
  id: RouterPhaseId;
  labelKey: string;
  detailKey: string;
  branchKeys?: string[];
}[] = [
  { id: "question", labelKey: "question", detailKey: "questionDetail" },
  {
    id: "manual",
    labelKey: "manual",
    detailKey: "manualDetail",
    branchKeys: ["manualIndex", "manualAdaptor", "manualOpenApi", "manualBoth"],
  },
  { id: "ragScore", labelKey: "ragScore", detailKey: "ragScoreDetail" },
  {
    id: "fastPath",
    labelKey: "fastPath",
    detailKey: "fastPathDetail",
    branchKeys: ["fastAdaptor", "fastIndex", "fastOpenApi", "fastFallthrough"],
  },
  {
    id: "phase1",
    labelKey: "phase1",
    detailKey: "phase1Detail",
    branchKeys: ["phase1Ok", "phase1Clarify", "phase1NoOs"],
  },
  {
    id: "phase2",
    labelKey: "phase2",
    detailKey: "phase2Detail",
    branchKeys: ["phase2Ok", "phase2Retry", "phase2Clarify"],
  },
  {
    id: "phase3",
    labelKey: "phase3",
    detailKey: "phase3Detail",
    branchKeys: ["phase3OpenApi", "phase3Both", "phase3Clarify"],
  },
  {
    id: "adaptorScope",
    labelKey: "adaptorScope",
    detailKey: "adaptorScopeDetail",
    branchKeys: ["scopeKeep", "scopeAdaptorWin", "scopeDropAdaptor"],
  },
  {
    id: "planner",
    labelKey: "planner",
    detailKey: "plannerDetail",
    branchKeys: ["intentOs", "intentAdaptor", "intentOpenApi", "intentMixed", "intentClarify"],
  },
  { id: "answer", labelKey: "answer", detailKey: "answerDetail" },
];

export const USE_CASE_FLOWS: UseCaseFlowDef[] = [
  {
    id: "sql-adaptors",
    titleKey: "sqlAdaptors",
    summaryKey: "sqlAdaptorsSummary",
    icon: "storage",
    path: [
      "ds-adaptors",
      "new-adaptor",
      "test-browse",
      "adaptor-discovery",
      "approve-analysis",
      "transfer-rag",
      "schema-relations",
      "publish-schema",
      "ai-report",
      "adaptor-query",
      "chat-answer",
    ],
    nodes: [
      { id: "ds-adaptors", labelKey: "dsAdaptorsTab", kind: "source", row: 0, lane: 1 },
      { id: "new-adaptor", labelKey: "newAdaptor", kind: "process", row: 1, lane: 1 },
      { id: "test-browse", labelKey: "testBrowseSchema", kind: "process", row: 2, lane: 1 },
      { id: "adaptor-discovery", labelKey: "adaptorDiscoveryTab", kind: "process", row: 3, lane: 1 },
      { id: "approve-analysis", labelKey: "approveAnalysis", kind: "decision", row: 4, lane: 1 },
      { id: "reject-draft", labelKey: "rejectDraft", kind: "branch", row: 4, lane: 2 },
      { id: "transfer-rag", labelKey: "transferToRag", kind: "store", row: 5, lane: 1 },
      { id: "schema-relations", labelKey: "schemaRelationsTab", kind: "process", row: 6, lane: 1 },
      { id: "publish-schema", labelKey: "publishToRag", kind: "store", row: 7, lane: 1 },
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 8, lane: 1, showRouter: true },
      { id: "clarify", labelKey: "clarificationCard", kind: "branch", row: 9, lane: 0 },
      { id: "adaptor-query", labelKey: "toolAdaptorQuery", kind: "tool", row: 9, lane: 1 },
      { id: "os-instead", labelKey: "toolOpenSearchInstead", kind: "branch", row: 9, lane: 2 },
      { id: "chat-answer", labelKey: "normalizedAnswer", kind: "result", row: 10, lane: 1 },
    ],
    edges: [
      { from: "ds-adaptors", to: "new-adaptor", variant: "primary" },
      { from: "new-adaptor", to: "test-browse", variant: "primary" },
      { from: "test-browse", to: "adaptor-discovery", variant: "primary" },
      { from: "adaptor-discovery", to: "approve-analysis", variant: "primary" },
      { from: "approve-analysis", to: "transfer-rag", variant: "primary", labelKey: "approve" },
      { from: "approve-analysis", to: "reject-draft", variant: "alt", labelKey: "reject" },
      { from: "transfer-rag", to: "schema-relations", variant: "primary" },
      { from: "schema-relations", to: "publish-schema", variant: "primary" },
      { from: "publish-schema", to: "ai-report", variant: "primary" },
      { from: "ai-report", to: "adaptor-query", variant: "primary", labelKey: "adaptorClearWin" },
      { from: "ai-report", to: "clarify", variant: "alt", labelKey: "routerClarify" },
      { from: "ai-report", to: "os-instead", variant: "alt", labelKey: "indexWins" },
      { from: "adaptor-query", to: "chat-answer", variant: "primary" },
      { from: "os-instead", to: "chat-answer", variant: "alt" },
    ],
  },
  {
    id: "opensearch-mapping",
    titleKey: "opensearchMapping",
    summaryKey: "opensearchMappingSummary",
    icon: "manage_search",
    path: [
      "matching-tasks",
      "discover-mapping",
      "approve-analysis",
      "transfer-rag",
      "schema-relations",
      "publish-schema",
      "ai-report",
      "os-tool",
      "chat-answer",
    ],
    nodes: [
      { id: "matching-tasks", labelKey: "matchingTasksTab", kind: "source", row: 0, lane: 1 },
      { id: "discover-mapping", labelKey: "discoverMapping", kind: "process", row: 1, lane: 1 },
      { id: "check-changes", labelKey: "checkMappingChanges", kind: "branch", row: 1, lane: 2 },
      { id: "approve-analysis", labelKey: "approveAnalysis", kind: "decision", row: 2, lane: 1 },
      { id: "reject-draft", labelKey: "rejectDraft", kind: "branch", row: 2, lane: 2 },
      { id: "transfer-rag", labelKey: "transferToRag", kind: "store", row: 3, lane: 1 },
      { id: "schema-relations", labelKey: "schemaRelationsTab", kind: "process", row: 4, lane: 1 },
      { id: "publish-schema", labelKey: "publishToRag", kind: "store", row: 5, lane: 1 },
      { id: "ingest-settings", labelKey: "opensearchSecurity", kind: "branch", row: 5, lane: 2 },
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 6, lane: 1, showRouter: true },
      { id: "clarify", labelKey: "clarificationCard", kind: "branch", row: 7, lane: 0 },
      { id: "os-tool", labelKey: "toolOpenSearch", kind: "tool", row: 7, lane: 1 },
      { id: "chat-answer", labelKey: "normalizedAnswer", kind: "result", row: 8, lane: 1 },
    ],
    edges: [
      { from: "matching-tasks", to: "discover-mapping", variant: "primary" },
      { from: "matching-tasks", to: "check-changes", variant: "alt", labelKey: "driftCheck" },
      { from: "check-changes", to: "discover-mapping", variant: "alt" },
      { from: "discover-mapping", to: "approve-analysis", variant: "primary" },
      { from: "approve-analysis", to: "transfer-rag", variant: "primary", labelKey: "approve" },
      { from: "approve-analysis", to: "reject-draft", variant: "alt", labelKey: "reject" },
      { from: "transfer-rag", to: "schema-relations", variant: "primary" },
      { from: "schema-relations", to: "publish-schema", variant: "primary" },
      { from: "publish-schema", to: "ai-report", variant: "primary" },
      { from: "ingest-settings", to: "ai-report", variant: "alt", labelKey: "allowlistGate" },
      { from: "ai-report", to: "os-tool", variant: "primary", labelKey: "indexClearWin" },
      { from: "ai-report", to: "clarify", variant: "alt", labelKey: "routerClarify" },
      { from: "os-tool", to: "chat-answer", variant: "primary" },
    ],
  },
  {
    id: "openapi-catalog",
    titleKey: "openapiCatalog",
    summaryKey: "openapiCatalogSummary",
    icon: "api",
    path: [
      "openapi-tab",
      "new-catalog",
      "auth-features",
      "snapshot-rag",
      "reanalyze-rag",
      "ai-report",
      "list-catalogs",
      "list-tags",
      "openapi-get",
      "show-result",
    ],
    nodes: [
      { id: "openapi-tab", labelKey: "openapiCatalogsTab", kind: "source", row: 0, lane: 1 },
      { id: "new-catalog", labelKey: "newCatalog", kind: "process", row: 1, lane: 1 },
      { id: "auth-features", labelKey: "catalogAuth", kind: "process", row: 2, lane: 1 },
      { id: "snapshot-rag", labelKey: "snapshotAndRag", kind: "store", row: 3, lane: 1 },
      { id: "reanalyze-rag", labelKey: "reanalyzeForRag", kind: "process", row: 4, lane: 1 },
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 5, lane: 1, showRouter: true },
      { id: "list-catalogs", labelKey: "toolOpenApiListCatalogs", kind: "tool", row: 6, lane: 1 },
      { id: "list-tags", labelKey: "toolOpenApiListTags", kind: "decision", row: 7, lane: 1 },
      { id: "openapi-get", labelKey: "toolOpenApiGet", kind: "tool", row: 8, lane: 1 },
      { id: "read-result", labelKey: "toolOpenApiReadResult", kind: "branch", row: 8, lane: 0 },
      { id: "propose-patch", labelKey: "toolOpenApiProposePatch", kind: "branch", row: 8, lane: 2 },
      { id: "mutation-approve", labelKey: "mutationDraftApprove", kind: "branch", row: 9, lane: 2 },
      { id: "show-result", labelKey: "showApiResult", kind: "result", row: 9, lane: 1 },
    ],
    edges: [
      { from: "openapi-tab", to: "new-catalog", variant: "primary" },
      { from: "new-catalog", to: "auth-features", variant: "primary" },
      { from: "auth-features", to: "snapshot-rag", variant: "primary" },
      { from: "snapshot-rag", to: "reanalyze-rag", variant: "primary" },
      { from: "reanalyze-rag", to: "ai-report", variant: "primary" },
      { from: "ai-report", to: "list-catalogs", variant: "primary", labelKey: "openapiMode" },
      { from: "list-catalogs", to: "list-tags", variant: "primary" },
      { from: "list-tags", to: "openapi-get", variant: "primary", labelKey: "pickerOrAgent" },
      { from: "openapi-get", to: "show-result", variant: "primary" },
      { from: "openapi-get", to: "read-result", variant: "alt", labelKey: "largePayload" },
      { from: "read-result", to: "show-result", variant: "alt" },
      { from: "openapi-get", to: "propose-patch", variant: "alt", labelKey: "writeIntent" },
      { from: "propose-patch", to: "mutation-approve", variant: "alt" },
      { from: "mutation-approve", to: "show-result", variant: "alt", labelKey: "approveReject" },
    ],
  },
  {
    id: "data-prep",
    titleKey: "dataPrep",
    summaryKey: "dataPrepSummary",
    icon: "account_tree",
    path: [
      "prep-jobs",
      "new-job",
      "add-sources",
      "join-edges",
      "output-node",
      "validate-test",
      "run-job",
      "index-approvals",
      "publish-analysis",
      "schema-relations",
      "ai-report",
      "live-read",
      "chat-answer",
    ],
    nodes: [
      { id: "prep-jobs", labelKey: "dataPrepJobsTab", kind: "source", row: 0, lane: 1 },
      { id: "new-job", labelKey: "newPrepJob", kind: "process", row: 1, lane: 1 },
      { id: "add-sources", labelKey: "prepAddSources", kind: "process", row: 2, lane: 1 },
      { id: "src-dsl", labelKey: "prepDslQuery", kind: "branch", row: 2, lane: 0 },
      { id: "src-adaptor", labelKey: "prepAdaptorNode", kind: "branch", row: 2, lane: 2 },
      { id: "src-openapi", labelKey: "prepOpenApiNode", kind: "branch", row: 2, lane: 3 },
      { id: "join-edges", labelKey: "prepJoinEdges", kind: "process", row: 3, lane: 1 },
      { id: "output-node", labelKey: "prepOutputNode", kind: "process", row: 4, lane: 1 },
      { id: "validate-test", labelKey: "prepValidateTest", kind: "decision", row: 5, lane: 1 },
      { id: "high-rows", labelKey: "prepHighRowDialog", kind: "branch", row: 5, lane: 2 },
      { id: "run-job", labelKey: "prepRun", kind: "tool", row: 6, lane: 1 },
      { id: "index-approvals", labelKey: "prepIndexApprovals", kind: "decision", row: 7, lane: 1 },
      { id: "reject-index", labelKey: "rejectDraft", kind: "branch", row: 7, lane: 2 },
      { id: "publish-analysis", labelKey: "prepPublishAnalysis", kind: "store", row: 8, lane: 1 },
      { id: "schema-relations", labelKey: "schemaRelationsTab", kind: "process", row: 9, lane: 1 },
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 10, lane: 1, showRouter: true },
      { id: "live-read", labelKey: "liveReadOutput", kind: "tool", row: 11, lane: 1 },
      { id: "chat-answer", labelKey: "normalizedAnswer", kind: "result", row: 12, lane: 1 },
    ],
    edges: [
      { from: "prep-jobs", to: "new-job", variant: "primary" },
      { from: "new-job", to: "add-sources", variant: "primary" },
      { from: "add-sources", to: "src-dsl", variant: "alt", labelKey: "plusDsl" },
      { from: "add-sources", to: "src-adaptor", variant: "alt", labelKey: "plusAdaptor" },
      { from: "add-sources", to: "src-openapi", variant: "alt", labelKey: "plusOpenApi" },
      { from: "src-dsl", to: "join-edges", variant: "alt" },
      { from: "src-adaptor", to: "join-edges", variant: "alt" },
      { from: "src-openapi", to: "join-edges", variant: "alt" },
      { from: "add-sources", to: "join-edges", variant: "primary" },
      { from: "join-edges", to: "output-node", variant: "primary" },
      { from: "output-node", to: "validate-test", variant: "primary" },
      { from: "validate-test", to: "run-job", variant: "primary" },
      { from: "validate-test", to: "high-rows", variant: "alt", labelKey: "highExpansion" },
      { from: "high-rows", to: "run-job", variant: "alt", labelKey: "runAnyway" },
      { from: "run-job", to: "index-approvals", variant: "primary" },
      { from: "index-approvals", to: "publish-analysis", variant: "primary", labelKey: "approve" },
      { from: "index-approvals", to: "reject-index", variant: "alt", labelKey: "reject" },
      { from: "publish-analysis", to: "schema-relations", variant: "primary" },
      { from: "schema-relations", to: "ai-report", variant: "primary" },
      { from: "ai-report", to: "live-read", variant: "primary" },
      { from: "live-read", to: "chat-answer", variant: "primary" },
    ],
  },
  {
    id: "examples-rag",
    titleKey: "examplesRag",
    summaryKey: "examplesRagSummary",
    icon: "library_books",
    path: [
      "ai-report",
      "executed-query",
      "save-example",
      "rag-ingest",
      "examples-tab",
      "next-message",
      "rag-guide",
      "reuse-shape",
    ],
    nodes: [
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 0, lane: 1, showRouter: true },
      { id: "executed-query", labelKey: "executedQueriesChip", kind: "tool", row: 1, lane: 1 },
      { id: "no-query", labelKey: "noQueryRecorded", kind: "branch", row: 1, lane: 2 },
      { id: "save-example", labelKey: "saveAsExample", kind: "process", row: 2, lane: 1 },
      { id: "already-saved", labelKey: "alreadySavedExample", kind: "branch", row: 2, lane: 2 },
      { id: "rag-ingest", labelKey: "ragIngestExample", kind: "store", row: 3, lane: 1 },
      { id: "examples-tab", labelKey: "chatQueryExamplesTab", kind: "store", row: 4, lane: 1 },
      { id: "next-message", labelKey: "nextChatMessage", kind: "chat", row: 5, lane: 1 },
      { id: "rag-guide", labelKey: "toolRagGuide", kind: "decision", row: 6, lane: 1 },
      { id: "reuse-shape", labelKey: "reuseQueryShape", kind: "result", row: 7, lane: 1 },
    ],
    edges: [
      { from: "ai-report", to: "executed-query", variant: "primary" },
      { from: "ai-report", to: "no-query", variant: "alt", labelKey: "noExecutedQuery" },
      { from: "executed-query", to: "save-example", variant: "primary" },
      { from: "save-example", to: "rag-ingest", variant: "primary", labelKey: "saveExample" },
      { from: "save-example", to: "already-saved", variant: "alt", labelKey: "duplicate409" },
      { from: "rag-ingest", to: "examples-tab", variant: "primary" },
      { from: "examples-tab", to: "next-message", variant: "primary" },
      { from: "next-message", to: "rag-guide", variant: "primary" },
      { from: "rag-guide", to: "reuse-shape", variant: "primary" },
    ],
  },
  {
    id: "widget-dashboard",
    titleKey: "widgetDashboard",
    summaryKey: "widgetDashboardSummary",
    icon: "dashboard",
    path: [
      "ai-report",
      "widget-builder",
      "widget-draft",
      "save-widget",
      "widgets-tab",
      "new-dashboard",
      "palette",
      "add-widget",
    ],
    nodes: [
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 0, lane: 1, showRouter: true },
      { id: "widget-builder", labelKey: "toolWidgetBuilder", kind: "tool", row: 1, lane: 1 },
      { id: "widget-draft", labelKey: "widgetDraftCard", kind: "decision", row: 2, lane: 1 },
      { id: "decline-draft", labelKey: "widgetDraftNo", kind: "branch", row: 2, lane: 0 },
      { id: "save-widget", labelKey: "widgetDraftYes", kind: "process", row: 3, lane: 1 },
      { id: "pending-index", labelKey: "widgetPendingApproval", kind: "branch", row: 3, lane: 2 },
      { id: "add-from-chat", labelKey: "addToDashboardChat", kind: "branch", row: 4, lane: 0 },
      { id: "widgets-tab", labelKey: "widgetsQueriesTab", kind: "store", row: 4, lane: 1 },
      { id: "new-dashboard", labelKey: "newDashboard", kind: "process", row: 5, lane: 1 },
      { id: "palette", labelKey: "widgetPalette", kind: "process", row: 6, lane: 1 },
      { id: "add-widget", labelKey: "addWidgetToGrid", kind: "result", row: 7, lane: 1 },
    ],
    edges: [
      { from: "ai-report", to: "widget-builder", variant: "primary" },
      { from: "widget-builder", to: "widget-draft", variant: "primary" },
      { from: "widget-draft", to: "save-widget", variant: "primary", labelKey: "yesSave" },
      { from: "widget-draft", to: "decline-draft", variant: "alt", labelKey: "noDecline" },
      { from: "save-widget", to: "widgets-tab", variant: "primary" },
      { from: "save-widget", to: "pending-index", variant: "alt", labelKey: "indexPending" },
      { from: "save-widget", to: "add-from-chat", variant: "alt", labelKey: "addToDashboard" },
      { from: "add-from-chat", to: "add-widget", variant: "alt" },
      { from: "widgets-tab", to: "new-dashboard", variant: "primary" },
      { from: "new-dashboard", to: "palette", variant: "primary" },
      { from: "palette", to: "add-widget", variant: "primary" },
      { from: "pending-index", to: "widgets-tab", variant: "alt", labelKey: "adminApproveIndex" },
    ],
  },
  {
    id: "anomaly-incident",
    titleKey: "anomalyIncident",
    summaryKey: "anomalyIncidentSummary",
    icon: "warning_amber",
    path: [
      "ai-report",
      "draft-definition",
      "mutation-approve",
      "definitions-edit",
      "sync-detector",
      "start-detector",
      "run-now",
      "incidents",
      "os-ad-tab",
    ],
    nodes: [
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 0, lane: 1, showRouter: true },
      { id: "draft-definition", labelKey: "toolAnomalyDraftDef", kind: "tool", row: 1, lane: 1 },
      { id: "mutation-approve", labelKey: "mutationDraftApprove", kind: "decision", row: 2, lane: 1 },
      { id: "mutation-reject", labelKey: "mutationDraftReject", kind: "branch", row: 2, lane: 2 },
      { id: "definitions-edit", labelKey: "anomalyDefinitionsEdit", kind: "process", row: 3, lane: 1 },
      { id: "manual-new", labelKey: "newAnomalyDefinition", kind: "branch", row: 3, lane: 0 },
      { id: "sync-detector", labelKey: "syncDetector", kind: "process", row: 4, lane: 1 },
      { id: "plugin-missing", labelKey: "adPluginMissing", kind: "branch", row: 4, lane: 2 },
      { id: "start-detector", labelKey: "startStopDetector", kind: "tool", row: 5, lane: 1 },
      { id: "run-now", labelKey: "anomalyRunNow", kind: "process", row: 6, lane: 1 },
      { id: "incidents", labelKey: "anomalyIncidentsTab", kind: "result", row: 7, lane: 1 },
      { id: "os-ad-tab", labelKey: "osAdDetectorsTab", kind: "tool", row: 8, lane: 1 },
    ],
    edges: [
      { from: "ai-report", to: "draft-definition", variant: "primary" },
      { from: "draft-definition", to: "mutation-approve", variant: "primary" },
      { from: "mutation-approve", to: "definitions-edit", variant: "primary", labelKey: "approve" },
      { from: "mutation-approve", to: "mutation-reject", variant: "alt", labelKey: "reject" },
      { from: "manual-new", to: "definitions-edit", variant: "alt", labelKey: "manualPath" },
      { from: "definitions-edit", to: "sync-detector", variant: "primary" },
      { from: "sync-detector", to: "start-detector", variant: "primary" },
      { from: "sync-detector", to: "plugin-missing", variant: "alt", labelKey: "pluginGate" },
      { from: "start-detector", to: "run-now", variant: "primary" },
      { from: "run-now", to: "incidents", variant: "primary" },
      { from: "incidents", to: "os-ad-tab", variant: "primary" },
    ],
  },
  {
    id: "insight-mail",
    titleKey: "insightMail",
    summaryKey: "insightMailSummary",
    icon: "mark_email_read",
    path: [
      "ai-report",
      "insight-draft",
      "insight-jobs",
      "edit-job",
      "enable-job",
      "run-or-cron",
      "ai-summary",
      "notification-mail",
    ],
    nodes: [
      { id: "ai-report", labelKey: "aiReportChat", kind: "chat", row: 0, lane: 1, showRouter: true },
      { id: "insight-draft", labelKey: "toolInsightJobDraft", kind: "tool", row: 1, lane: 1 },
      { id: "insight-jobs", labelKey: "insightJobsPage", kind: "store", row: 2, lane: 1 },
      { id: "edit-job", labelKey: "editInsightJob", kind: "process", row: 3, lane: 1 },
      { id: "enable-job", labelKey: "enableInsightJob", kind: "decision", row: 4, lane: 1 },
      { id: "dry-run", labelKey: "insightDryRun", kind: "branch", row: 4, lane: 0 },
      { id: "run-or-cron", labelKey: "insightRunOrCron", kind: "process", row: 5, lane: 1 },
      { id: "ai-summary", labelKey: "insightAiSummary", kind: "tool", row: 6, lane: 1 },
      { id: "notification-mail", labelKey: "notificationMail", kind: "result", row: 7, lane: 1 },
      { id: "mail-fail", labelKey: "mailNotSent", kind: "branch", row: 7, lane: 2 },
    ],
    edges: [
      { from: "ai-report", to: "insight-draft", variant: "primary" },
      { from: "insight-draft", to: "insight-jobs", variant: "primary", labelKey: "createdDisabled" },
      { from: "insight-jobs", to: "edit-job", variant: "primary" },
      { from: "edit-job", to: "enable-job", variant: "primary" },
      { from: "enable-job", to: "run-or-cron", variant: "primary", labelKey: "enabled" },
      { from: "enable-job", to: "dry-run", variant: "alt", labelKey: "dryRun" },
      { from: "dry-run", to: "ai-summary", variant: "alt" },
      { from: "run-or-cron", to: "ai-summary", variant: "primary" },
      { from: "ai-summary", to: "notification-mail", variant: "primary" },
      { from: "ai-summary", to: "mail-fail", variant: "alt", labelKey: "smtpMissing" },
    ],
  },
];

export const USE_CASE_FLOW_IDS = USE_CASE_FLOWS.map((f) => f.id);

export function getUseCaseFlow(id: string | undefined): UseCaseFlowDef {
  return USE_CASE_FLOWS.find((f) => f.id === id) ?? USE_CASE_FLOWS[0];
}

export function isUseCaseFlowId(value: string | undefined): value is UseCaseFlowId {
  return Boolean(value && USE_CASE_FLOW_IDS.includes(value as UseCaseFlowId));
}

export const USE_CASE_FLOW_TOC = USE_CASE_FLOWS.map((f) => ({
  id: f.id,
  titleKey: f.titleKey,
}));

export function nodeById(flow: UseCaseFlowDef, id: string) {
  return flow.nodes.find((n) => n.id === id);
}
